import React from "react";
import {
  Box,
  Card,
  Dropdown,
  Grid,
  IconButton,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Sheet,
  Tooltip,
} from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import {
  AddIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarViewDayIcon,
  CalendarViewWeekIcon,
  CalendarViewMonthIcon,
  TodayIcon,
} from "../../styles/StyledMUI";
import DarkTypography from "./DarkTypography";
import IconButtonCTA from "./IconButtonCTA";
import {
  colorTokens,
  ctaAccentColor,
  ctaAccentHoverColor,
  dayCellAddBtnSx,
  dayCellMenuSx,
  hrCardSx,
  hrSheetSx,
  listRowIconButtonPlainSx,
  withAlpha,
} from "../../styles/globalStyles";

export type ViewMode = "day" | "week" | "month";

export type PlanningControlsProps = {
  periodTitle: string;
  viewMode: ViewMode;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onViewModeChange: (mode: ViewMode) => void;
  viewModes?: ViewMode[];
  /** Extra controls in the date navigation row (e.g. filters menu). */
  actionsSlot?: React.ReactNode;
};

const DEFAULT_VIEW_MODES: ViewMode[] = ["day", "week", "month"];

const planningToolbarTouchSx: SxProps = {
  minHeight: { xs: 36, sm: 36 },
  minWidth: { xs: 36, sm: 36 },
  flexShrink: 0,
  "& svg, & .MuiSvgIcon-root": {
    fontSize: { xs: "1.1rem", sm: "1.2rem" },
  },
};

const planningViewModeToggleSx = (
  active: boolean,
  /** Merged last so responsive `display` is not overridden by active/inactive colors */
  layout: { display: { xs: string; sm: string } },
): SxProps => ({
  ...listRowIconButtonPlainSx,
  ...planningToolbarTouchSx,
  flexShrink: 0,
  ...(active
    ? {
        color: `${ctaAccentColor} !important`,
        "--Icon-color": `${ctaAccentColor} !important`,
        backgroundColor: withAlpha(ctaAccentColor, 0.16),
        "& .MuiSvgIcon-root": { color: `${ctaAccentColor} !important` },
        "&:hover": {
          backgroundColor: withAlpha(ctaAccentColor, 0.24),
          color: `${ctaAccentHoverColor} !important`,
          "--Icon-color": `${ctaAccentHoverColor} !important`,
          "& .MuiSvgIcon-root": { color: `${ctaAccentHoverColor} !important` },
        },
      }
    : {
        color: `${colorTokens.text.muted} !important`,
        "--Icon-color": `${colorTokens.text.muted} !important`,
        backgroundColor: "transparent",
        "& .MuiSvgIcon-root": { color: `${colorTokens.text.muted} !important` },
        "&:hover": {
          backgroundColor: colorTokens.bg.hover,
          color: `${colorTokens.text.primary} !important`,
          "--Icon-color": `${colorTokens.text.primary} !important`,
          "& .MuiSvgIcon-root": { color: `${colorTokens.text.primary} !important` },
        },
      }),
  ...layout,
});

export function PlanningControls({
  periodTitle,
  viewMode,
  onPrev,
  onNext,
  onToday,
  onViewModeChange,
  viewModes = DEFAULT_VIEW_MODES,
  actionsSlot,
}: PlanningControlsProps) {
  const dateNav = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.25, sm: 0.5 },
        flexWrap: "nowrap",
        justifyContent: "center",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <IconButtonCTA
        aria-label="Période précédente"
        ctaVariant="accent"
        size="sm"
        onClick={onPrev}
        sx={planningToolbarTouchSx}
      >
        <ChevronLeftIcon />
      </IconButtonCTA>
      <DarkTypography
        level="title-lg"
        sx={{
          flex: "0 1 auto",
          minWidth: 0,
          maxWidth: { xs: 160, sm: 280 },
          textAlign: "center",
          fontSize: { xs: "0.8rem", sm: "1rem" },
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          px: { xs: 0.25, sm: 0.5 },
        }}
      >
        {periodTitle}
      </DarkTypography>
      <IconButtonCTA
        aria-label="Période suivante"
        ctaVariant="accent"
        size="sm"
        onClick={onNext}
        sx={planningToolbarTouchSx}
      >
        <ChevronRightIcon />
      </IconButtonCTA>
    </Box>
  );

  // Kept outside dateNav so the title stays geometrically centered between [<] and [>].
  const todayButton = (
    <Tooltip title="Aujourd'hui">
      <IconButtonCTA
        aria-label="Aller à aujourd'hui"
        ctaVariant="accent"
        size="sm"
        onClick={onToday}
        sx={planningToolbarTouchSx}
      >
        <TodayIcon />
      </IconButtonCTA>
    </Tooltip>
  );

  const viewModeButtons = (
    <Box sx={{ display: "flex", gap: { xs: 0.25, sm: 0.5 }, flexShrink: 0 }}>
      {viewModes.includes("day") && (
        <Tooltip title="Jour">
          <IconButton
            aria-label="Vue jour"
            variant="plain"
            color="neutral"
            onClick={() => onViewModeChange("day")}
            size="sm"
            sx={planningViewModeToggleSx(viewMode === "day", {
              display: { xs: "inline-flex", sm: "inline-flex" },
            })}
          >
            <CalendarViewDayIcon />
          </IconButton>
        </Tooltip>
      )}
      {viewModes.includes("week") && (
        <Tooltip title="Semaine">
          <IconButton
            aria-label="Vue semaine"
            variant="plain"
            color="neutral"
            onClick={() => onViewModeChange("week")}
            size="sm"
            sx={planningViewModeToggleSx(viewMode === "week", {
              display: { xs: "none", sm: "inline-flex" },
            })}
          >
            <CalendarViewWeekIcon />
          </IconButton>
        </Tooltip>
      )}
      {viewModes.includes("month") && (
        <Tooltip title="Mois">
          <IconButton
            aria-label="Vue mois"
            variant="plain"
            color="neutral"
            onClick={() => onViewModeChange("month")}
            size="sm"
            sx={planningViewModeToggleSx(viewMode === "month", {
              display: { xs: "none", sm: "inline-flex" },
            })}
          >
            <CalendarViewMonthIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Card sx={{ p: { xs: 0.5, sm: 1.5 }, mb: { xs: 1, sm: 1.5 }, ...hrCardSx, maxWidth: "100%" }}>
      {/* Single-row layout at every breakpoint: views (left) | period (center) | actions (right). */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
          alignItems: "center",
          columnGap: { xs: 0.5, sm: 1.5 },
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            justifySelf: "start",
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.25, sm: 0.5 },
          }}
        >
          {viewModeButtons}
          {todayButton}
        </Box>
        <Box sx={{ justifySelf: "center", minWidth: 0, maxWidth: "100%" }}>{dateNav}</Box>
        {actionsSlot ? (
          <Box
            sx={{
              justifySelf: "end",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              minWidth: 0,
            }}
          >
            {actionsSlot}
          </Box>
        ) : (
          <Box sx={{ minWidth: 0 }} aria-hidden />
        )}
      </Box>
    </Card>
  );
}

const PIXELS_PER_HOUR_AGENDA = 60;

export type PlanningCalendarGridProps = {
  viewMode: "day" | "week" | "month";
  days: Date[];
  monthGridOffset: number;
  renderDayContent: (day: Date, isToday: boolean, isWeekend: boolean) => React.ReactNode;
  showTimeSlots?: boolean;
  hoursStart?: number;
  hoursEnd?: number;
  viewportHoursStart?: number;
  viewportHoursEnd?: number;
  businessHoursStart?: number;
  businessHoursEnd?: number;
  renderTimeSlotContent?: (
    day: Date,
    hour: number,
    isToday: boolean,
    isWeekend: boolean,
  ) => React.ReactNode;
  renderWeekAgendaDay?: (
    day: Date,
    columnHeightPx: number,
    pixelsPerHour: number,
    isToday: boolean,
    isWeekend: boolean,
  ) => React.ReactNode;
  renderDayHeaderFooter?: (day: Date, isToday: boolean, isWeekend: boolean) => React.ReactNode;
  dayHeaderFormat?: "default" | "dayMonth";
  fillHeight?: boolean;
  allDaysWorking?: boolean;
  /** Actions shown via a "+" popover on each day cell. */
  dayCellActions?: DayCellAction[];
  /** @deprecated Use dayCellActions instead. */
  onDayCellClick?: (day: Date) => void;
  autoScrollToToday?: boolean;
};

export type DayCellAction = {
  key: string;
  label: string;
  emoji: string;
  onAction: (day: Date) => void;
};

const isWeekendDay = (day: Date) => day.getDay() === 7;

const DEFAULT_BUSINESS_START = 7;
const DEFAULT_BUSINESS_END = 18;

const todayHighlight = colorTokens.text.title;

function PlanningDayHeaderFooterRow(props: {
  day: Date;
  isToday: boolean;
  isWeekend: boolean;
  renderDayHeaderFooter?: (day: Date, isToday: boolean, isWeekend: boolean) => React.ReactNode;
}) {
  const { day, isToday, isWeekend, renderDayHeaderFooter } = props;

  if (!renderDayHeaderFooter) {
    return null;
  }

  const node = renderDayHeaderFooter(day, isToday, isWeekend);

  if (node == null) {
    return null;
  }

  return (
    <Box
      sx={{
        borderTop: `1px solid ${colorTokens.border.default}`,
        mt: 0.5,
        pt: 0.35,
        pb: 0.1,
        minWidth: 0,
        width: "100%",
      }}
    >
      {node}
    </Box>
  );
}

function agendaTodayColumnBg(isToday: boolean, isWeekend: boolean): string | undefined {
  if (!isToday) return undefined;
  return withAlpha(todayHighlight, isWeekend ? 0.12 : 0.08);
}

function DayCellActionsMenu({
  day,
  dayCellActions,
  ariaLabel = "Actions du jour",
}: {
  day: Date;
  dayCellActions?: DayCellAction[];
  ariaLabel?: string;
}) {
  if (dayCellActions == null || dayCellActions.length === 0) return null;

  return (
    <Dropdown>
      <MenuButton
        slots={{ root: IconButton }}
        slotProps={{
          root: {
            size: "sm",
            variant: "plain",
            "aria-label": ariaLabel,
            sx: dayCellAddBtnSx,
          },
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <AddIcon sx={{ fontSize: 16 }} />
      </MenuButton>
      <Menu placement="bottom-end" size="sm" sx={dayCellMenuSx}>
        {dayCellActions.map((action) => (
          <MenuItem key={action.key} onClick={() => action.onAction(day)}>
            <ListItemDecorator sx={{ minWidth: 24 }}>{action.emoji}</ListItemDecorator>
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </Dropdown>
  );
}

function DayCellCard({
  day,
  isToday,
  isWeekend,
  viewMode,
  dayHeaderFormat,
  dayCellActions,
  renderDayHeaderFooter,
  renderDayContent,
}: {
  day: Date;
  isToday: boolean;
  isWeekend: boolean;
  viewMode: PlanningCalendarGridProps["viewMode"];
  dayHeaderFormat: "default" | "dayMonth";
  dayCellActions?: DayCellAction[];
  renderDayHeaderFooter?: (day: Date, isToday: boolean, isWeekend: boolean) => React.ReactNode;
  renderDayContent: (day: Date, isToday: boolean, isWeekend: boolean) => React.ReactNode;
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        minWidth: 0,
        backgroundColor: isWeekend ? colorTokens.bg.surfaceElevated : colorTokens.bg.surface,
        border: isToday ? `2px solid ${todayHighlight}` : `1px solid ${colorTokens.border.strong}`,
        p: { xs: 1, sm: 1 },
        ...(viewMode === "month"
          ? {
              transition:
                "border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease",
              "@media (hover: hover)": {
                "&:hover": {
                  borderColor: colorTokens.accent.primary,
                  boxShadow: `0 4px 14px ${withAlpha(colorTokens.bg.overlay, 0.35)}`,
                  backgroundColor: isWeekend
                    ? withAlpha(colorTokens.accent.primary, 0.08)
                    : withAlpha(colorTokens.accent.primary, 0.05),
                },
              },
            }
          : {}),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: { xs: 1, sm: 1 },
          gap: { xs: 1, sm: 0.5 },
        }}
      >
        <DarkTypography
          level="body-sm"
          sx={{
            flex: 1,
            color: isToday
              ? todayHighlight
              : isWeekend
                ? colorTokens.text.muted
                : colorTokens.text.primary,
            fontWeight: isToday ? "bold" : "normal",
            fontSize: { xs: "1rem", sm: "0.875rem" },
          }}
        >
          {dayHeaderFormat === "dayMonth"
            ? `${day.toLocaleDateString("fr-FR", { weekday: "long" })} ${day.getDate()} ${day.toLocaleDateString("fr-FR", { month: "long" })}`
            : day.getDate()}
        </DarkTypography>
        {(viewMode === "week" || viewMode === "day") && dayHeaderFormat === "default" && (
          <DarkTypography
            component="span"
            sx={{
              color: isToday
                ? todayHighlight
                : isWeekend
                  ? colorTokens.text.muted
                  : colorTokens.text.primary,
              fontSize: { xs: "0.9rem", sm: "0.8em" },
              fontWeight: "bold",
            }}
          >
            {day.toLocaleDateString("fr-FR", { weekday: "long" })}
          </DarkTypography>
        )}
        <DayCellActionsMenu day={day} dayCellActions={dayCellActions} />
      </Box>
      <PlanningDayHeaderFooterRow
        day={day}
        isToday={isToday}
        isWeekend={isWeekend}
        renderDayHeaderFooter={renderDayHeaderFooter}
      />
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.75, sm: 0.5 }, minWidth: 0 }}
      >
        {renderDayContent(day, isToday, isWeekend)}
      </Box>
    </Card>
  );
}

function PlanningCalendarGridComponent({
  viewMode,
  days,
  monthGridOffset,
  renderDayContent,
  showTimeSlots = false,
  hoursStart = 0,
  hoursEnd = 24,
  viewportHoursStart,
  viewportHoursEnd,
  businessHoursStart = DEFAULT_BUSINESS_START,
  businessHoursEnd = DEFAULT_BUSINESS_END,
  renderTimeSlotContent,
  renderWeekAgendaDay,
  renderDayHeaderFooter,
  dayHeaderFormat = "default",
  fillHeight = false,
  allDaysWorking = false,
  dayCellActions,
  onDayCellClick,
  autoScrollToToday = false,
}: PlanningCalendarGridProps) {
  const todayCellRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!autoScrollToToday || !todayCellRef.current) return;
    todayCellRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [autoScrollToToday, days]);

  const getIsWeekend = (day: Date): boolean => (allDaysWorking ? false : isWeekendDay(day));
  const useAgenda =
    showTimeSlots && (viewMode === "week" || viewMode === "day") && renderWeekAgendaDay != null;
  const useTimeSlots =
    showTimeSlots &&
    (viewMode === "week" || viewMode === "day") &&
    renderTimeSlotContent != null &&
    !useAgenda;
  const hours = React.useMemo(() => {
    const h: number[] = [];
    for (let i = hoursStart; i < hoursEnd; i++) h.push(i);
    return h;
  }, [hoursStart, hoursEnd]);

  const hoursCount = hoursEnd - hoursStart;
  const vpStart = viewportHoursStart ?? hoursStart;
  const vpEnd = viewportHoursEnd ?? hoursEnd;
  const viewportHoursCount = vpEnd - vpStart;
  const useScrollViewport =
    useAgenda && viewportHoursCount > 0 && (vpStart > hoursStart || vpEnd < hoursEnd);

  const defaultAgendaHeight = hoursCount * PIXELS_PER_HOUR_AGENDA;
  const [contentHeight, setContentHeight] = React.useState(defaultAgendaHeight);
  const [scrollViewportHeight, setScrollViewportHeight] = React.useState(
    viewportHoursCount * PIXELS_PER_HOUR_AGENDA,
  );
  const contentAreaRef = React.useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    if (!useAgenda) return;
    if (useScrollViewport) {
      const el = scrollContainerRef.current;
      if (!el) return;
      const update = () => {
        const h = el.clientHeight;
        if (h > 0) setScrollViewportHeight(h);
      };
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    const el = contentAreaRef.current;
    if (!el) return;
    const updateHeight = () => {
      const h = el.clientHeight;
      if (h > 0) setContentHeight(h);
    };
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [useAgenda, useScrollViewport, days.length, hoursStart, hoursEnd]);

  React.useEffect(() => {
    if (!useScrollViewport || !scrollContainerRef.current || viewportHoursCount <= 0) return;
    const pxPerHour = scrollViewportHeight / viewportHoursCount;
    scrollContainerRef.current.scrollTop = (vpStart - hoursStart) * pxPerHour;
  }, [useScrollViewport, scrollViewportHeight, viewportHoursCount, vpStart, hoursStart]);

  const agendaHeight = useAgenda
    ? useScrollViewport
      ? hoursCount * (scrollViewportHeight / viewportHoursCount)
      : contentHeight
    : (hoursEnd - hoursStart) * PIXELS_PER_HOUR_AGENDA;
  const pixelsPerHour =
    useAgenda && hoursCount > 0
      ? useScrollViewport
        ? scrollViewportHeight / viewportHoursCount
        : contentHeight / hoursCount
      : PIXELS_PER_HOUR_AGENDA;

  if (useAgenda && useScrollViewport) {
    const totalContentHeight = hoursCount * pixelsPerHour;
    const stickyHeaderBg = (isWeekend: boolean) =>
      isWeekend ? colorTokens.bg.surfaceElevated : colorTokens.bg.surface;
    return (
      <Sheet
        sx={{
          ...hrSheetSx,
          overflow: "hidden",
          p: { xs: 1, sm: 2 },
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            scrollbarGutter: "stable",
            border: `1px solid ${colorTokens.border.default}`,
            borderRadius: "sm",
            backgroundColor: colorTokens.bg.surface,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))`,
              gridTemplateRows: `auto ${totalContentHeight}px`,
              minWidth: 0,
              width: "100%",
              backgroundColor: colorTokens.bg.surface,
            }}
          >
            <Box
              sx={{
                gridColumn: 1,
                gridRow: 1,
                position: "sticky",
                top: 0,
                zIndex: 3,
                p: 0.5,
                backgroundColor: colorTokens.bg.surface,
                borderBottom: `1px solid ${colorTokens.border.default}`,
                borderRight: `1px solid ${colorTokens.border.default}`,
              }}
            />
            {days.map((day, colIndex) => {
              const isToday = day.toDateString() === new Date().toDateString();
              const isWeekend = getIsWeekend(day);
              return (
                <Box
                  key={`hdr-${colIndex}`}
                  sx={{
                    gridColumn: colIndex + 2,
                    gridRow: 1,
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor:
                      agendaTodayColumnBg(isToday, isWeekend) ?? stickyHeaderBg(isWeekend),
                    borderBottom: `1px solid ${colorTokens.border.default}`,
                    borderLeft: isToday
                      ? `2px solid ${todayHighlight}`
                      : colIndex > 0
                        ? `1px solid ${colorTokens.border.default}`
                        : "none",
                    borderRight: isToday ? `2px solid ${todayHighlight}` : "none",
                    p: 0.75,
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <DarkTypography
                      level="body-sm"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        color: isToday
                          ? todayHighlight
                          : isWeekend
                            ? colorTokens.text.muted
                            : colorTokens.text.primary,
                        fontWeight: isToday ? "bold" : "normal",
                      }}
                    >
                      {day.toLocaleDateString("fr-FR", { weekday: "long" })} {day.getDate()}{" "}
                      {day.toLocaleDateString("fr-FR", { month: "long" })}
                    </DarkTypography>
                    <DayCellActionsMenu day={day} dayCellActions={dayCellActions} />
                  </Box>
                  <PlanningDayHeaderFooterRow
                    day={day}
                    isToday={isToday}
                    isWeekend={isWeekend}
                    renderDayHeaderFooter={renderDayHeaderFooter}
                  />
                </Box>
              );
            })}
            <Box
              sx={{
                gridColumn: 1,
                gridRow: 2,
                borderRight: `1px solid ${colorTokens.border.default}`,
                backgroundColor: colorTokens.bg.surface,
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
                minHeight: totalContentHeight,
              }}
            >
              {hours.map((hour) => {
                const isBusinessHour = hour >= businessHoursStart && hour < businessHoursEnd;
                return (
                  <Box
                    key={hour}
                    sx={{
                      height: pixelsPerHour,
                      minHeight: pixelsPerHour,
                      flexShrink: 0,
                      borderBottom: `1px solid ${colorTokens.bg.surfaceElevated}`,
                      px: 0.5,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-end",
                      backgroundColor: isBusinessHour
                        ? undefined
                        : withAlpha(colorTokens.bg.overlay, 0.25),
                    }}
                  >
                    <DarkTypography
                      level="body-xs"
                      sx={{
                        color: isBusinessHour ? colorTokens.text.muted : colorTokens.border.strong,
                        fontSize: "0.7rem",
                      }}
                    >
                      {hour}h
                    </DarkTypography>
                  </Box>
                );
              })}
            </Box>
            {days.map((day, colIndex) => {
              const isToday = day.toDateString() === new Date().toDateString();
              const isWeekend = getIsWeekend(day);
              const totalHours = hoursEnd - hoursStart;
              const businessStartOffset = Math.max(0, businessHoursStart - hoursStart);
              const businessEndOffset = Math.min(totalHours, businessHoursEnd - hoursStart);
              const pctNonOuvreesTop =
                totalHours > 0 ? (businessStartOffset / totalHours) * 100 : 0;
              const pctNonOuvreesBottom =
                totalHours > 0 ? ((totalHours - businessEndOffset) / totalHours) * 100 : 0;
              return (
                <Box
                  key={colIndex}
                  sx={{
                    gridColumn: colIndex + 2,
                    gridRow: 2,
                    minHeight: totalContentHeight,
                    borderLeft: isToday
                      ? `2px solid ${todayHighlight}`
                      : colIndex > 0
                        ? `1px solid ${colorTokens.border.default}`
                        : "none",
                    borderRight: isToday ? `2px solid ${todayHighlight}` : "none",
                    backgroundColor:
                      agendaTodayColumnBg(isToday, isWeekend) ??
                      (isWeekend ? colorTokens.bg.surfaceElevated : colorTokens.bg.surface),
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none",
                      background: `linear-gradient(to bottom, ${withAlpha(colorTokens.bg.overlay, 0.2)} 0%, ${withAlpha(colorTokens.bg.overlay, 0.2)} ${pctNonOuvreesTop}%, transparent ${pctNonOuvreesTop}%, transparent ${100 - pctNonOuvreesBottom}%, ${withAlpha(colorTokens.bg.overlay, 0.2)} ${100 - pctNonOuvreesBottom}%, ${withAlpha(colorTokens.bg.overlay, 0.2)} 100%)`,
                    },
                  }}
                >
                  {renderWeekAgendaDay(day, agendaHeight, pixelsPerHour, isToday, isWeekend)}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Sheet>
    );
  }

  if (useAgenda) {
    return (
      <Sheet
        sx={{
          ...hrSheetSx,
          overflow: "hidden",
          p: { xs: 1, sm: 2 },
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))`,
            gridTemplateRows: "auto 1fr",
            gap: 0,
            flex: 1,
            minHeight: 0,
            border: `1px solid ${colorTokens.border.default}`,
            borderRadius: "sm",
            overflow: "hidden",
            backgroundColor: colorTokens.bg.surface,
          }}
        >
          <Box
            sx={{
              gridColumn: 1,
              gridRow: 1,
              p: 0.5,
              backgroundColor: colorTokens.bg.surface,
              borderBottom: `1px solid ${colorTokens.border.default}`,
            }}
          />
          {days.map((day, colIndex) => {
            const isToday = day.toDateString() === new Date().toDateString();
            const isWeekend = getIsWeekend(day);
            return (
              <Box
                key={colIndex}
                sx={{
                  gridColumn: colIndex + 2,
                  gridRow: 1,
                  backgroundColor:
                    agendaTodayColumnBg(isToday, isWeekend) ??
                    (isWeekend ? colorTokens.bg.surfaceElevated : colorTokens.bg.surface),
                  borderBottom: `1px solid ${colorTokens.border.default}`,
                  borderLeft: isToday
                    ? `2px solid ${todayHighlight}`
                    : colIndex > 0
                      ? `1px solid ${colorTokens.border.default}`
                      : "none",
                  borderRight: isToday ? `2px solid ${todayHighlight}` : "none",
                  p: 0.75,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    minWidth: 0,
                  }}
                >
                  <DarkTypography
                    level="body-sm"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: isToday
                        ? todayHighlight
                        : isWeekend
                          ? colorTokens.text.muted
                          : colorTokens.text.primary,
                      fontWeight: isToday ? "bold" : "normal",
                    }}
                  >
                    {day.toLocaleDateString("fr-FR", { weekday: "long" })} {day.getDate()}{" "}
                    {day.toLocaleDateString("fr-FR", { month: "long" })}
                  </DarkTypography>
                  <DayCellActionsMenu day={day} dayCellActions={dayCellActions} />
                </Box>
                <PlanningDayHeaderFooterRow
                  day={day}
                  isToday={isToday}
                  isWeekend={isWeekend}
                  renderDayHeaderFooter={renderDayHeaderFooter}
                />
              </Box>
            );
          })}
          <Box
            sx={{
              gridColumn: 1,
              gridRow: 2,
              borderRight: `1px solid ${colorTokens.border.default}`,
              backgroundColor: colorTokens.bg.surface,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {hours.map((hour) => {
              const isBusinessHour = hour >= businessHoursStart && hour < businessHoursEnd;
              return (
                <Box
                  key={hour}
                  sx={{
                    flex: "1 1 0",
                    minHeight: 0,
                    borderBottom: `1px solid ${colorTokens.bg.surfaceElevated}`,
                    px: 0.5,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                    backgroundColor: isBusinessHour
                      ? undefined
                      : withAlpha(colorTokens.bg.overlay, 0.25),
                  }}
                >
                  <DarkTypography
                    level="body-xs"
                    sx={{
                      color: isBusinessHour ? colorTokens.text.muted : colorTokens.border.strong,
                      fontSize: "0.7rem",
                    }}
                  >
                    {hour}h
                  </DarkTypography>
                </Box>
              );
            })}
          </Box>
          {days.map((day, colIndex) => {
            const isToday = day.toDateString() === new Date().toDateString();
            const isWeekend = getIsWeekend(day);
            const totalHours = hoursEnd - hoursStart;
            const businessStartOffset = Math.max(0, businessHoursStart - hoursStart);
            const businessEndOffset = Math.min(totalHours, businessHoursEnd - hoursStart);
            const pctNonOuvreesTop = totalHours > 0 ? (businessStartOffset / totalHours) * 100 : 0;
            const pctNonOuvreesBottom =
              totalHours > 0 ? ((totalHours - businessEndOffset) / totalHours) * 100 : 0;
            return (
              <Box
                key={colIndex}
                ref={colIndex === 0 ? contentAreaRef : undefined}
                sx={{
                  gridColumn: colIndex + 2,
                  gridRow: 2,
                  minHeight: 0,
                  borderLeft: isToday
                    ? `2px solid ${todayHighlight}`
                    : colIndex > 0
                      ? `1px solid ${colorTokens.border.default}`
                      : "none",
                  borderRight: isToday ? `2px solid ${todayHighlight}` : "none",
                  backgroundColor:
                    agendaTodayColumnBg(isToday, isWeekend) ??
                    (isWeekend ? colorTokens.bg.surfaceElevated : colorTokens.bg.surface),
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background: `linear-gradient(to bottom, ${withAlpha(colorTokens.bg.overlay, 0.2)} 0%, ${withAlpha(colorTokens.bg.overlay, 0.2)} ${pctNonOuvreesTop}%, transparent ${pctNonOuvreesTop}%, transparent ${100 - pctNonOuvreesBottom}%, ${withAlpha(colorTokens.bg.overlay, 0.2)} ${100 - pctNonOuvreesBottom}%, ${withAlpha(colorTokens.bg.overlay, 0.2)} 100%)`,
                  },
                }}
              >
                {renderWeekAgendaDay(day, agendaHeight, pixelsPerHour, isToday, isWeekend)}
              </Box>
            );
          })}
        </Box>
      </Sheet>
    );
  }

  if (useTimeSlots) {
    return (
      <Sheet
        sx={{
          ...hrSheetSx,
          overflow: "auto",
          p: { xs: 1, sm: 2 },
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))`,
            gridTemplateRows: `auto repeat(${hours.length}, minmax(44px, auto))`,
            gap: "1px",
            minHeight: { xs: "300px", sm: "400px" },
            pb: 2,
          }}
        >
          <Box
            sx={{ gridColumn: 1, gridRow: 1, p: 0.5, backgroundColor: colorTokens.bg.surface }}
          />
          {days.map((day, colIndex) => {
            const isToday = day.toDateString() === new Date().toDateString();
            const isWeekend = getIsWeekend(day);
            return (
              <Box
                key={colIndex}
                sx={{
                  gridColumn: colIndex + 2,
                  gridRow: 1,
                  backgroundColor: isWeekend
                    ? colorTokens.bg.surfaceElevated
                    : colorTokens.bg.surface,
                  border: `1px solid ${colorTokens.border.strong}`,
                  p: 0.5,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    minWidth: 0,
                  }}
                >
                  <DarkTypography
                    level="body-sm"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      color: isToday
                        ? todayHighlight
                        : isWeekend
                          ? colorTokens.text.muted
                          : colorTokens.text.primary,
                      fontWeight: isToday ? "bold" : "normal",
                    }}
                  >
                    {day.toLocaleDateString("fr-FR", { weekday: "long" })} {day.getDate()}{" "}
                    {day.toLocaleDateString("fr-FR", { month: "long" })}
                  </DarkTypography>
                  <DayCellActionsMenu day={day} dayCellActions={dayCellActions} />
                </Box>
                <PlanningDayHeaderFooterRow
                  day={day}
                  isToday={isToday}
                  isWeekend={isWeekend}
                  renderDayHeaderFooter={renderDayHeaderFooter}
                />
              </Box>
            );
          })}
          {hours.map((hour, rowIndex) => (
            <React.Fragment key={hour}>
              <Box
                sx={{
                  gridColumn: 1,
                  gridRow: rowIndex + 2,
                  py: 0.5,
                  px: 0.5,
                  backgroundColor: colorTokens.bg.surface,
                  border: `1px solid ${colorTokens.border.default}`,
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <DarkTypography level="body-xs" sx={{ color: colorTokens.text.muted }}>
                  {hour}h
                </DarkTypography>
              </Box>
              {days.map((day, colIndex) => {
                const isToday = day.toDateString() === new Date().toDateString();
                const isWeekend = getIsWeekend(day);
                return (
                  <Box
                    key={`${hour}-${colIndex}`}
                    sx={{
                      gridColumn: colIndex + 2,
                      gridRow: rowIndex + 2,
                      minHeight: 44,
                      overflow: "visible",
                      backgroundColor: isWeekend
                        ? colorTokens.bg.surfaceElevated
                        : colorTokens.bg.surface,
                      border: isToday
                        ? `1px solid ${todayHighlight}`
                        : `1px solid ${colorTokens.border.default}`,
                      p: 0.25,
                    }}
                  >
                    {renderTimeSlotContent(day, hour, isToday, isWeekend)}
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      </Sheet>
    );
  }

  return (
    <Sheet
      sx={{
        ...hrSheetSx,
        overflow: fillHeight ? "hidden" : "auto",
        p: { xs: 1, sm: 2 },
        flex: 1,
        minHeight: 0,
        display: fillHeight ? "flex" : "block",
        flexDirection: fillHeight ? "column" : undefined,
      }}
    >
      <Grid
        container
        spacing={{ xs: 0.5, sm: 1 }}
        sx={{
          minHeight: fillHeight ? 0 : { xs: "300px", sm: viewMode === "month" ? "600px" : "400px" },
          flex: fillHeight ? 1 : undefined,
          pb: { xs: 1, sm: 2 },
          overflow: fillHeight ? "auto" : undefined,
        }}
      >
        {viewMode === "month" && monthGridOffset > 0 && (
          <Grid md={(12 / 7) * monthGridOffset} sx={{ display: { xs: "none", md: "block" } }} />
        )}
        {days.map((day, index) => {
          const isToday = day.toDateString() === new Date().toDateString();
          const isWeekend = getIsWeekend(day);

          return (
            <Grid
              key={index}
              ref={isToday ? todayCellRef : undefined}
              xs={12}
              md={12 / 7}
              sx={{ minHeight: { xs: "auto", sm: "100px" } }}
            >
              <DayCellCard
                day={day}
                isToday={isToday}
                isWeekend={isWeekend}
                viewMode={viewMode}
                dayHeaderFormat={dayHeaderFormat}
                dayCellActions={dayCellActions}
                renderDayHeaderFooter={renderDayHeaderFooter}
                renderDayContent={renderDayContent}
              />
            </Grid>
          );
        })}
      </Grid>
    </Sheet>
  );
}

export const PlanningCalendarGrid = React.memo(PlanningCalendarGridComponent);
